import { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Crown, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { API } from "@/App";

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 12;

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

export default function SubscriptionSuccessPage() {
  const sessionId = useQueryParam("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling" }); // polling | active | failed
  const attempts = useRef(0);
  const cancelled = useRef(false);

  useEffect(() => {
    document.title = "Activating your subscription…";
    if (!sessionId) {
      setState({ status: "failed", reason: "Missing session ID" });
      return;
    }

    const poll = async () => {
      if (cancelled.current) return;
      attempts.current += 1;

      try {
        const res = await axios.get(`${API}/subscription/checkout/status/${sessionId}`, {
          withCredentials: true,
        });
        const data = res.data;

        if (data.status === "active") {
          setState({ status: "active", ...data });
          toast.success(
            data.is_founding_member
              ? `Welcome, Founding Member #${data.founding_member_number}! 👑`
              : "You're now Premium. Welcome aboard!"
          );
          return;
        }
        if (data.status === "failed") {
          setState({ status: "failed", reason: data.reason || "Payment was not completed" });
          return;
        }
      } catch (e) {
        // Short-circuit on auth/not-found errors — retrying won't help
        const code = e?.response?.status;
        if (code === 401 || code === 403) {
          setState({ status: "failed", reason: "Please sign in again to confirm your subscription." });
          return;
        }
        if (code === 404) {
          setState({ status: "failed", reason: "We couldn't find that checkout session." });
          return;
        }
        // network error or transient — try again
      }

      if (attempts.current >= MAX_ATTEMPTS) {
        setState({ status: "failed", reason: "Timed out waiting for confirmation" });
        return;
      }
      setTimeout(poll, POLL_INTERVAL);
    };

    poll();
    return () => { cancelled.current = true; };
    // Only sessionId should re-trigger the poll. API/POLL_INTERVAL/MAX_ATTEMPTS are
    // module constants; refs and setters are stable.
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-tbc-cream flex flex-col">
      <Navbar />

      <div className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-tbc-navy/10 p-8 sm:p-10 text-center shadow-sm" data-testid="subscription-success-card">
          {state.status === "polling" && (
            <>
              <Loader2 className="w-12 h-12 text-tbc-gold animate-spin mx-auto" />
              <h1 className="font-heading text-2xl text-tbc-navy mt-6">Activating your subscription…</h1>
              <p className="text-tbc-navy/70 mt-2 text-sm">
                We&apos;re confirming with Stripe. This usually takes a few seconds.
              </p>
            </>
          )}

          {state.status === "active" && (
            <>
              {state.is_founding_member ? (
                <div className="w-16 h-16 rounded-full bg-tbc-gold mx-auto flex items-center justify-center">
                  <Crown className="w-8 h-8 text-tbc-navy" />
                </div>
              ) : (
                <CheckCircle2 className="w-16 h-16 text-tbc-gold mx-auto" />
              )}
              <h1 className="font-heading text-3xl text-tbc-navy mt-6">
                {state.is_founding_member ? "You're a Founding Member!" : "You're Premium!"}
              </h1>
              {state.is_founding_member && (
                <p className="mt-2 text-tbc-gold font-semibold" data-testid="founding-member-confirm">
                  Member #{state.founding_member_number} of 100 · Locked in for life
                </p>
              )}
              <p className="text-tbc-navy/70 mt-3">
                Featured placement, gold badge, unlimited products, analytics, and more are now active on your listing.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/dashboard")}
                  data-testid="go-to-dashboard"
                  className="rounded-full bg-tbc-gold hover:bg-tbc-gold/90 text-tbc-navy font-semibold"
                >
                  Go to Dashboard
                </Button>
                {state.business_id && (
                  <Link
                    to={`/business/${state.business_id}`}
                    className="inline-flex items-center justify-center rounded-full border border-tbc-navy/20 text-tbc-navy px-6 py-2 text-sm font-medium hover:bg-tbc-navy hover:text-white transition-colors"
                  >
                    View My Profile
                  </Link>
                )}
              </div>
            </>
          )}

          {state.status === "failed" && (
            <>
              <XCircle className="w-12 h-12 text-red-500 mx-auto" />
              <h1 className="font-heading text-2xl text-tbc-navy mt-6">Subscription not confirmed</h1>
              <p className="text-tbc-navy/70 mt-2 text-sm">
                {state.reason || "We couldn't confirm your subscription."} If you were charged, contact support and we&apos;ll sort it out.
              </p>
              <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                <Button
                  onClick={() => navigate("/pricing")}
                  data-testid="retry-pricing"
                  className="rounded-full bg-tbc-navy hover:bg-tbc-navy/90 text-white"
                >
                  Back to Pricing
                </Button>
              </div>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
